/**
 * Componente reutilizável para seleção de variáveis globais
 * Permite buscar, selecionar e criar variáveis inline
 */

import { useState } from 'react';
import { Check, ChevronsUpDown, Plus, Edit2, Trash2, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { cn } from '@/lib/utils';
import { TenantVariable, useTenantVariables } from '@/hooks/useTenantVariables';

interface VariablePickerComboboxProps {
  tenantId: string;
  value?: string; // variable_id ou name
  onChange: (variable: TenantVariable | null) => void; // Aceita null para limpar
  filterType?: string[]; // Filtrar por tipos específicos
  allowCreate?: boolean; // Permitir criar nova variável inline
  allowClear?: boolean; // Permitir limpar seleção
  placeholder?: string;
  /**
   * Oculta variáveis de sistema (is_system = true).
   * Usar em campos de DESTINO DE ESCRITA (ex.: "Salvar resultado em variável"):
   * variáveis de sistema são calculadas pelo motor a cada execução e servem
   * apenas para leitura; gravar sobre elas sobrescreveria valores como
   * contact_name no contexto da execução, gerando comportamento enganoso.
   */
  excludeSystem?: boolean;
}

/**
 * Componente de seleção de variáveis com busca e criação inline
 */
export function VariablePickerCombobox({
  tenantId,
  value,
  onChange,
  filterType,
  allowCreate = true,
  allowClear = true,
  placeholder = 'Selecione ou crie uma variável...',
  excludeSystem = false,
}: VariablePickerComboboxProps) {
  const [open, setOpen] = useState(false);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newVarName, setNewVarName] = useState('');
  const [newVarType, setNewVarType] = useState<TenantVariable['type']>('text');
  const [newVarDescription, setNewVarDescription] = useState('');

  const { 
    variables, 
    isLoading, 
    createVariableAsync,
    deleteVariable,
    isCreating 
  } = useTenantVariables(tenantId);

  // Filtra variáveis por tipo se especificado.
  // Quando excludeSystem = true (campos de destino de escrita), remove também
  // as variáveis de sistema, pois elas são calculadas pelo motor (somente leitura).
  const filteredVariables = variables.filter(v => {
    if (excludeSystem && v.is_system) return false;
    if (filterType && filterType.length > 0 && !filterType.includes(v.type)) return false;
    return true;
  });

  // Encontra variável selecionada
  const selectedVariable = variables.find(v => v.id === value || v.name === value);

  /**
   * Cria nova variável inline
   */
  const handleCreateVariable = async () => {
    if (!newVarName.trim()) return;

    try {
      const newVar = await createVariableAsync({
        name: newVarName.trim().toLowerCase().replace(/\s+/g, '_'),
        display_name: newVarName.trim(),
        type: newVarType,
        description: newVarDescription.trim() || undefined,
      });

      // Seleciona a variável recém-criada
      onChange(newVar);
      
      // Reset form
      setNewVarName('');
      setNewVarType('text');
      setNewVarDescription('');
      setShowCreateForm(false);
      setOpen(false);
    } catch (error) {
      console.error('Erro ao criar variável:', error);
    }
  };

  /**
   * Deleta variável (apenas não-sistema)
   */
  const handleDeleteVariable = (varId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    deleteVariable(varId);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <div className="relative w-full">
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={open}
            className="w-full justify-between"
            disabled={isLoading}
          >
            {selectedVariable ? (
              <span className="flex items-center gap-2 flex-1 min-w-0">
                {selectedVariable.is_system && (
                  <span className="text-xs bg-primary/10 text-primary px-1.5 py-0.5 rounded shrink-0">
                    SISTEMA
                  </span>
                )}
                <span className="truncate">
                  {selectedVariable.display_name || selectedVariable.name}
                </span>
              </span>
            ) : (
              <span className="text-muted-foreground">{placeholder}</span>
            )}
            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        
        {/* Botão de limpar seleção - fora do trigger para evitar conflito */}
        {allowClear && selectedVariable && (
          <button
            type="button"
            className="absolute right-8 top-1/2 -translate-y-1/2 p-1 text-muted-foreground hover:text-destructive z-10"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onChange(null);
            }}
            aria-label="Limpar seleção"
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>
      <PopoverContent className="w-[400px] p-0" align="start">
        {!showCreateForm ? (
          <Command>
            <CommandInput placeholder="Buscar variável..." />
            <CommandList>
              <CommandEmpty>Nenhuma variável encontrada.</CommandEmpty>
              <CommandGroup heading="Variáveis Disponíveis">
                {filteredVariables.map((variable) => (
                  <CommandItem
                    key={variable.id}
                    value={variable.name}
                    onSelect={() => {
                      onChange(variable);
                      setOpen(false);
                    }}
                    className="flex items-center justify-between"
                  >
                    <div className="flex items-center gap-2">
                      <Check
                        className={cn(
                          'h-4 w-4',
                          value === variable.id || value === variable.name
                            ? 'opacity-100'
                            : 'opacity-0'
                        )}
                      />
                      <div className="flex flex-col">
                        <span className="flex items-center gap-2">
                          {variable.display_name || variable.name}
                          {variable.is_system && (
                            <span className="text-xs bg-primary/10 text-primary px-1.5 py-0.5 rounded">
                              SISTEMA
                            </span>
                          )}
                        </span>
                        {variable.description && (
                          <span className="text-xs text-muted-foreground">
                            {variable.description}
                          </span>
                        )}
                      </div>
                    </div>
                    
                    {!variable.is_system && (
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-6 w-6"
                        onClick={(e) => handleDeleteVariable(variable.id, e)}
                      >
                        <Trash2 className="h-3 w-3 text-destructive" />
                      </Button>
                    )}
                  </CommandItem>
                ))}
              </CommandGroup>
              
              {allowCreate && (
                <CommandGroup>
                  <CommandItem
                    onSelect={() => setShowCreateForm(true)}
                    className="flex items-center gap-2 text-primary"
                  >
                    <Plus className="h-4 w-4" />
                    <span>Criar nova variável</span>
                  </CommandItem>
                </CommandGroup>
              )}
            </CommandList>
          </Command>
        ) : (
          <div className="p-4 space-y-4">
            <div className="flex items-center justify-between">
              <h4 className="font-semibold">Nova Variável</h4>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setShowCreateForm(false)}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>

            <div className="space-y-2">
              <Label>Nome da Variável *</Label>
              <Input
                placeholder="ex: nome_cliente"
                value={newVarName}
                onChange={(e) => setNewVarName(e.target.value)}
                maxLength={50}
              />
              <p className="text-xs text-muted-foreground">
                Use letras minúsculas e underscores. Será convertido automaticamente.
              </p>
            </div>

            <div className="space-y-2">
              <Label>Tipo de Dado</Label>
              <Select value={newVarType} onValueChange={(v: any) => setNewVarType(v)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="text">Texto</SelectItem>
                  <SelectItem value="number">Número</SelectItem>
                  <SelectItem value="boolean">Booleano</SelectItem>
                  <SelectItem value="date">Data</SelectItem>
                  <SelectItem value="email">Email</SelectItem>
                  <SelectItem value="phone">Telefone</SelectItem>
                  <SelectItem value="cpf">CPF</SelectItem>
                  <SelectItem value="cnpj">CNPJ</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Descrição (opcional)</Label>
              <Input
                placeholder="Descrição da variável"
                value={newVarDescription}
                onChange={(e) => setNewVarDescription(e.target.value)}
                maxLength={200}
              />
            </div>

            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={() => setShowCreateForm(false)}
                className="flex-1"
              >
                Cancelar
              </Button>
              <Button
                onClick={handleCreateVariable}
                disabled={!newVarName.trim() || isCreating}
                className="flex-1"
              >
                {isCreating ? 'Criando...' : 'Criar'}
              </Button>
            </div>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}